import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IncidentReportForm from '../components/IncidentReportForm';
import IncidentViewModal from '../components/IncidentViewModal';
import { getApiBaseUrl } from '../utils/config';
import { motion, AnimatePresence } from 'framer-motion';
import {
  EyeIcon,
  CheckIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  CalendarIcon,
  MapPinIcon,
  XMarkIcon,
  DocumentTextIcon,
  TagIcon,
  CheckBadgeIcon,
  PaperClipIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Incidents: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    district: '',
    priority: '',
    status: '',
    incidentType: '',
    dateFrom: '',
    dateTo: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20); // Default 20 items per page

  // Utility functions for professional number formatting
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
  };

  const formatNumberWithCommas = (num: number): string => {
    return num.toLocaleString();
  };

  // Fetch incidents from API (scope by coordinator's district)
  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('token');

      // Add pagination parameters to fetch all incidents
      const url = new URL(`${getApiBaseUrl()}/incidents`);
      url.searchParams.append('limit', '1000'); // Request up to 1000 incidents
      url.searchParams.append('page', '1');
      url.searchParams.append('per_page', '1000'); // Alternative parameter name
      // If coordinator, restrict to their district at the API level
      if (user?.role === 'coordinator' && user.district) {
        url.searchParams.append('district', user.district);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 Full API Response:', result);
      console.log('🔍 Response keys:', Object.keys(result));
      console.log('🔍 Response.data:', result.data);
      console.log('🔍 Response.incidents:', result.incidents);
      console.log('🔍 Response.pagination:', result.pagination);
      console.log('🔍 Response.meta:', result.meta);
      console.log('🔍 Response.total:', result.total);

      // Handle different response formats
      const incidentsData = result.data || result.incidents || result || [];
      console.log('📊 Incidents data array length:', Array.isArray(incidentsData) ? incidentsData.length : 'Not an array');
      setIncidents(Array.isArray(incidentsData) ? incidentsData : []);

    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Failed to load incidents');
      // Keep empty array on error
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If coordinator, lock the district filter to their district
    if (user?.role === 'coordinator' && user.district) {
      setFilters((prev) => ({ ...prev, district: user.district as string }));
    }
    fetchIncidents();
  }, [token, user?.role, user?.district]);

  const handleNewIncident = () => {
    setEditingIncident(null);
    setShowIncidentForm(true);
  };

  const handleEditIncident = (incident: any) => {
    setEditingIncident(incident);
    setShowIncidentForm(true);
  };

  const handleIncidentFormClose = () => {
    setShowIncidentForm(false);
    setEditingIncident(null);
  };

  const handleIncidentFormSubmit = (formData: any) => {
    console.log('Incident form submitted:', formData);
    toast.success('Incident report submitted successfully!');
    setShowIncidentForm(false);
    setEditingIncident(null);
    // Refresh the incidents list
    fetchIncidents();
  };

  const handleViewIncident = (incident: any) => {
    setSelectedIncident(incident);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedIncident(null);
  };

  // Filter incidents based on current filters
  const filteredIncidents = useMemo(() => {
    return incidents.filter(incident => {
      const matchesSearch = !filters.search ||
        incident.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
        incident.description?.toLowerCase().includes(filters.search.toLowerCase());

      const matchesDistrict = !filters.district || incident.district === filters.district;
      const matchesPriority = !filters.priority || incident.severity === filters.priority.toLowerCase();
      const matchesStatus = !filters.status || incident.status === filters.status;
      const matchesType = !filters.incidentType || incident.category?.includes(filters.incidentType);

      const incidentDate = new Date(incident.created_at || incident.date);
      const matchesDateFrom = !filters.dateFrom || incidentDate >= new Date(filters.dateFrom);
      const matchesDateTo = !filters.dateTo || incidentDate <= new Date(filters.dateTo);

      return matchesSearch && matchesDistrict && matchesPriority &&
        matchesStatus && matchesType && matchesDateFrom && matchesDateTo;
    });
  }, [incidents, filters]);

  // Pagination calculations
  const totalFilteredIncidents = filteredIncidents.length;
  const totalPages = Math.ceil(totalFilteredIncidents / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedIncidents = filteredIncidents.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Calculate statistics from filtered data
  const stats = useMemo(() => {
    const total = filteredIncidents.length;
    const critical = filteredIncidents.filter(i => i.severity === 'critical').length;
    const high = filteredIncidents.filter(i => i.severity === 'high').length;
    const pending = filteredIncidents.filter(i => i.status === 'pending').length;
    const underReview = filteredIncidents.filter(i => i.status === 'under_review').length;
    const resolved = filteredIncidents.filter(i => i.status === 'resolved').length;

    // Incident types breakdown
    const typeBreakdown = filteredIncidents.reduce((acc, incident) => {
      const type = incident.category || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // District breakdown
    const districtBreakdown = filteredIncidents.reduce((acc, incident) => {
      const district = incident.district || 'Unknown';
      acc[district] = (acc[district] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total,
      critical,
      high,
      pending,
      underReview,
      resolved,
      typeBreakdown,
      districtBreakdown
    };
  }, [filteredIncidents]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      district: '',
      priority: '',
      status: '',
      incidentType: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const exportToExcel = () => {
    // Create CSV content
    const headers = ['ID', 'Title', 'Description', 'Type', 'Sub Category', 'Priority', 'Status', 'District', 'Location', 'Date', 'Created At', 'Attachments'];
    const csvContent = [
      headers.join(','),
      ...filteredIncidents.map(incident => [
        incident.id,
        `"${incident.title || ''}"`,
        `"${incident.description || ''}"`,
        `"${incident.category || ''}"`,
        `"${incident.subcategory || ''}"`,
        incident.severity || '',
        incident.status || '',
        incident.district || '',
        `"${incident.center || incident.location || ''}"`,
        incident.date || '',
        incident.created_at || incident.createdAt || '',
        incident.files?.length || incident.attachments || 0
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `incidents_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Incidents report exported successfully!');
  };

  const getUniqueValues = (key: string) => {
    return Array.from(new Set(incidents.map(incident => {
      if (key === 'incidentType') {
        return incident.category;
      }
      if (key === 'priority') {
        return incident.severity;
      }
      return incident[key as keyof typeof incident];
    }).filter(Boolean))).sort();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getIncidentStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Show incident form if it's open
  if (showIncidentForm) {
    return (
      <IncidentReportForm
        incident={editingIncident}
        onClose={handleIncidentFormClose}
        onSubmit={handleIncidentFormSubmit}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-lg p-8 text-center bg-white shadow-xl rounded-2xl">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl">
              <ExclamationTriangleIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="mb-4 text-2xl font-bold text-gray-800">Loading Incidents</h2>
          <div className="w-12 h-12 mx-auto mb-4 border-b-2 border-red-500 rounded-full animate-spin"></div>
          <p className="text-gray-600">Fetching incident reports...</p>
        </div>
      </div>
    );
  }

  // Access control: only admin and coordinator can view this page
  if (user && !['admin', 'coordinator'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-lg p-8 text-center bg-white shadow-xl rounded-2xl">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl">
              <ExclamationTriangleIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
          <p className="text-gray-600">This page is available to administrators and coordinators only.</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-6 py-3 text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Professional Header */}
      <div className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-lg border-gray-200/50">
        <div className="px-6 py-8 mx-auto max-w-7xl">
          <div className="flex flex-col justify-between mb-8 lg:flex-row lg:items-center">
            <div className="mb-6 lg:mb-0">
              <div className="flex items-center mb-2 space-x-3">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="flex items-center px-4 py-2 space-x-2 text-gray-600 transition-colors bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-900"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                  <span>Back</span>
                </button>
                <div className="p-2 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl">
                  <ExclamationTriangleIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">
                    {user?.role === 'coordinator' && user.district
                      ? `Incidents - ${user.district}`
                      : 'Incident Reports'}
                  </h1>
                  <p className="text-lg font-medium text-gray-600">
                    Comprehensive incident monitoring & management system
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">


              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <span className="flex items-center space-x-2">
                    <FunnelIcon className="w-4 h-4" />
                    <span>Filters</span>
                  </span>
                </button>
                <button
                  onClick={exportToExcel}
                  className="px-6 py-3 text-sm font-semibold text-gray-700 transition-all duration-300 bg-gray-100 border border-gray-200 rounded-xl hover:bg-gray-200"
                >
                  <span className="flex items-center space-x-2">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    <span>Export</span>
                  </span>
                </button>
                {user?.role === 'admin' && (
                  <button
                    onClick={handleNewIncident}
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <span className="flex items-center space-x-2">
                      <PlusIcon className="w-4 h-4" />
                      <span>New Report</span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8 mx-auto max-w-7xl">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-12"
          >

            {/* Professional Filters Panel */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:border-purple-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 group-hover:opacity-100"></div>

                {/* Header */}
                <div className="relative p-6 bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                        <FunnelIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Advanced Filters</h3>
                        <p className="text-sm text-white/80">Refine your incident search</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="p-2 text-white transition-colors rounded-lg bg-white/20 hover:bg-white/30"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="relative p-8">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">Search</label>
                      <input
                        type="text"
                        placeholder="Search title or description..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">District</label>
                      <select
                        value={filters.district}
                        onChange={(e) => handleFilterChange('district', e.target.value)}
                        disabled={user?.role === 'coordinator'}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white disabled:opacity-60"
                      >
                        <option value="">All Districts</option>
                        {getUniqueValues('district').map(district => (
                          <option key={district} value={district}>{district}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">Priority</label>
                      <select
                        value={filters.priority}
                        onChange={(e) => handleFilterChange('priority', e.target.value)}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white"
                      >
                        <option value="">All Priorities</option>
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">Status</label>
                      <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white"
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="under_review">Under Review</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">Incident Type</label>
                      <select
                        value={filters.incidentType}
                        onChange={(e) => handleFilterChange('incidentType', e.target.value)}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white"
                      >
                        <option value="">All Types</option>
                        <option value="Security">Security</option>
                        <option value="Elections">Elections</option>
                        <option value="Gender">Gender</option>
                        <option value="Health and Environment">Health & Environment</option>
                        <option value="Governance and Human Rights Violations">Governance & HR</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">Date From</label>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-700">Date To</label>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:bg-white"
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={clearFilters}
                        className="w-full px-6 py-3 text-sm font-semibold text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-gray-600 to-gray-700 rounded-xl hover:from-gray-700 hover:to-gray-800 hover:shadow-xl"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Professional Statistics Cards */}
            <div className="relative">
              <div className="flex items-center mb-8 space-x-4">
                <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl">
                  <ChartBarIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Incident Statistics</h2>
                  <p className="font-medium text-gray-600">
                    {filteredIncidents.length === incidents.length ?
                      `Real-time overview of all ${formatNumberWithCommas(stats.total)} incidents` :
                      `Filtered view: ${formatNumberWithCommas(stats.total)} of ${formatNumberWithCommas(incidents.length)} incidents`
                    }
                  </p>
                </div>
              </div>
              <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-red-500 to-orange-600"></div>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-blue-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-blue-50 to-indigo-100 group-hover:opacity-100"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 shadow-lg rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600">
                      <ExclamationTriangleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-green-600">Live</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-indigo-700">
                      {formatNumber(stats.total)}
                    </p>
                    <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                      {filteredIncidents.length === incidents.length ? 'Total Incidents' : 'Filtered Incidents'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumberWithCommas(stats.total)} {filteredIncidents.length !== incidents.length && `of ${formatNumberWithCommas(incidents.length)}`}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-red-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-red-50 to-red-100 group-hover:opacity-100"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 shadow-lg rounded-xl bg-gradient-to-r from-red-500 to-red-600">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
                        <span className="text-sm font-bold text-white">!</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-red-600">Critical</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-red-700">
                      {formatNumber(stats.critical)}
                    </p>
                    <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                      Critical Priority
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumberWithCommas(stats.critical)} urgent cases
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-orange-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-orange-50 to-orange-100 group-hover:opacity-100"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 shadow-lg rounded-xl bg-gradient-to-r from-orange-500 to-orange-600">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
                        <span className="text-sm font-bold text-white">H</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-orange-600">High</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-orange-700">
                      {formatNumber(stats.high)}
                    </p>
                    <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                      High Priority
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumberWithCommas(stats.high)} important cases
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-yellow-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-yellow-50 to-yellow-100 group-hover:opacity-100"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 shadow-lg rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600">
                      <CalendarIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-yellow-600">Pending</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-yellow-700">
                      {formatNumber(stats.pending)}
                    </p>
                    <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                      Pending Review
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumberWithCommas(stats.pending)} awaiting action
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-purple-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-purple-50 to-purple-100 group-hover:opacity-100"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 shadow-lg rounded-xl bg-gradient-to-r from-purple-500 to-purple-600">
                      <EyeIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-purple-600">Review</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-purple-700">
                      {formatNumber(stats.underReview)}
                    </p>
                    <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                      Under Review
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumberWithCommas(stats.underReview)} being processed
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-green-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-green-50 to-green-100 group-hover:opacity-100"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 shadow-lg rounded-xl bg-gradient-to-r from-green-500 to-green-600">
                      <CheckIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-green-600">Resolved</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-green-700">
                      {formatNumber(stats.resolved)}
                    </p>
                    <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                      Resolved Cases
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumberWithCommas(stats.resolved)} completed
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Professional Analytics Section */}
            <div className="mt-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="relative"
              >
                <div className="flex items-center mb-8 space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl">
                    <ChartPieIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Incident Analytics</h2>
                    <p className="font-medium text-gray-600">Detailed breakdown and insights</p>
                  </div>
                </div>
                <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-purple-500 to-indigo-600"></div>
              </motion.div>

              <div className="grid gap-8 mt-8 lg:grid-cols-3">
                {/* Professional Incident Types Chart */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="relative overflow-hidden bg-white border shadow-lg backdrop-blur-sm border-gray-200/70 rounded-2xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-pink-100/50"></div>
                  <div className="relative p-6">
                    <div className="flex items-center mb-6 space-x-3">
                      <div className="p-3 shadow-lg bg-gradient-to-r from-red-500 to-pink-600 rounded-xl">
                        <ChartBarIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Incidents by Type</h3>
                        <p className="text-sm text-gray-600">Category breakdown</p>
                      </div>
                    </div>

                    {/* Professional Pie Chart */}
                    <div className="mb-6">
                      <div className="relative w-48 h-48 mx-auto">
                        <svg viewBox="0 0 200 200" className="w-full h-full transform rotate-90">
                          {(() => {
                            let offset = 0;
                            return Object.entries(stats.typeBreakdown).map(([type, count], index) => {
                              const numericCount = Number(count);
                              const percentage = stats.total > 0 ? (numericCount / stats.total) : 0;
                              const colors = ['#EF4444', '#8B5CF6', '#EC4899', '#3B82F6', '#F97316'];
                              const radius = 80;
                              const circumference = 2 * Math.PI * radius;
                              const strokeDasharray = circumference * percentage;
                              const strokeDashoffset = -offset;

                              offset += strokeDasharray;

                              return (
                                <motion.circle
                                  key={type}
                                  cx="100"
                                  cy="100"
                                  r={radius}
                                  fill="transparent"
                                  stroke={colors[index] || '#6B7280'}
                                  strokeWidth="20"
                                  strokeDasharray={`${strokeDasharray} ${circumference}`}
                                  strokeDashoffset={strokeDashoffset}
                                  className="transition-all duration-300 hover:stroke-width-[25] filter drop-shadow-sm"
                                  initial={{ strokeDasharray: `0 ${circumference}` }}
                                  animate={{ strokeDasharray: `${strokeDasharray} ${circumference}` }}
                                  transition={{ duration: 1.5, delay: 0.5 + index * 0.2 }}
                                />
                              );
                            });
                          })()}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.total)}</div>
                            <div className="text-xs text-gray-500">Total</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 overflow-y-auto max-h-40">
                      {Object.entries(stats.typeBreakdown).map(([type, count], index) => {
                        const numericCount = Number(count);
                        const percentage = stats.total > 0 ? (numericCount / stats.total * 100) : 0;
                        const colors = ['bg-gradient-to-r from-red-500 to-red-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-pink-500 to-pink-600', 'bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-orange-500 to-orange-600'];
                        return (
                          <motion.div
                            key={type}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1 + index * 0.1 }}
                            className="flex items-center justify-between p-2 transition-all duration-200 rounded-lg bg-white/50 hover:bg-white/80"
                          >
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${colors[index] || 'bg-gray-500'} shadow-sm`}></div>
                              <span className="text-sm font-medium text-gray-700 truncate">{type}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-gray-900">{formatNumberWithCommas(numericCount)}</div>
                              <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>

                {/* Professional Districts Chart */}
                {user?.role !== 'coordinator' && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                    className="relative overflow-hidden bg-white border shadow-lg backdrop-blur-sm border-gray-200/70 rounded-2xl"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-cyan-100/50"></div>
                    <div className="relative p-6">
                      <div className="flex items-center mb-6 space-x-3">
                        <div className="p-3 shadow-lg bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
                          <MapPinIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Incidents by District</h3>
                          <p className="text-sm text-gray-600">Geographic distribution</p>
                        </div>
                      </div>

                      {/* Professional Pie Chart */}
                      <div className="mb-6">
                        <div className="relative w-48 h-48 mx-auto">
                          <svg viewBox="0 0 200 200" className="w-full h-full transform rotate-90">
                            {(() => {
                              let offset = 0;
                              return Object.entries(stats.districtBreakdown)
                                .sort(([, a], [, b]) => Number(b) - Number(a))
                                .slice(0, 5)
                                .map(([district, count], index) => {
                                  const numericCount = Number(count);
                                  const percentage = stats.total > 0 ? (numericCount / stats.total) : 0;
                                  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
                                  const radius = 80;
                                  const circumference = 2 * Math.PI * radius;
                                  const strokeDasharray = circumference * percentage;
                                  const strokeDashoffset = -offset;

                                  offset += strokeDasharray;

                                  return (
                                    <motion.circle
                                      key={district}
                                      cx="100"
                                      cy="100"
                                      r={radius}
                                      fill="transparent"
                                      stroke={colors[index]}
                                      strokeWidth="20"
                                      strokeDasharray={`${strokeDasharray} ${circumference}`}
                                      strokeDashoffset={strokeDashoffset}
                                      className="transition-all duration-300 hover:stroke-width-[25] filter drop-shadow-sm"
                                      initial={{ strokeDasharray: `0 ${circumference}` }}
                                      animate={{ strokeDasharray: `${strokeDasharray} ${circumference}` }}
                                      transition={{ duration: 1.5, delay: 0.7 + index * 0.2 }}
                                    />
                                  );
                                });
                            })()}
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-xl font-bold text-gray-900">Top 5</div>
                              <div className="text-xs text-gray-500">Districts</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Professional Scrollable List */}
                      <div className="h-64 overflow-y-auto border rounded-xl bg-gradient-to-b from-white/80 to-gray-50/80 backdrop-blur-sm">
                        <div className="p-3 space-y-2">
                          {Object.entries(stats.districtBreakdown)
                            .sort(([, a], [, b]) => Number(b) - Number(a))
                            .map(([district, count], index) => {
                              const numericCount = Number(count);
                              const percentage = stats.total > 0 ? (numericCount / stats.total * 100) : 0;
                              const colors = ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-yellow-500 to-yellow-600', 'bg-gradient-to-r from-red-500 to-red-600', 'bg-gradient-to-r from-purple-500 to-purple-600'];
                              const colorClass = index < 5 ? colors[index] : 'bg-gradient-to-r from-gray-400 to-gray-500';

                              return (
                                <motion.div
                                  key={district}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 1.2 + index * 0.05 }}
                                  className="p-3 transition-all duration-200 bg-white border shadow-sm rounded-xl hover:shadow-md hover:-translate-y-0.5"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                      <div className={`w-3 h-3 rounded-full mr-3 shadow-sm ${colorClass}`}></div>
                                      <span className="text-sm font-semibold text-gray-800">{district}</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-900">{formatNumberWithCommas(numericCount)}</span>
                                  </div>
                                  <div className="w-full h-2 bg-gray-200 rounded-full">
                                    <motion.div
                                      className={`h-2 rounded-full shadow-inner ${colorClass}`}
                                      initial={{ width: 0 }}
                                      animate={{ width: `${Math.max(percentage, 2)}%` }}
                                      transition={{ duration: 1, delay: 1.5 + index * 0.05 }}
                                    />
                                  </div>
                                  <div className="mt-2 text-xs font-medium text-gray-600">{percentage.toFixed(1)}% of total incidents</div>
                                </motion.div>
                              );
                            })
                          }
                          {Object.keys(stats.districtBreakdown).length === 0 && (
                            <div className="py-8 text-center text-gray-500">
                              <MapPinIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm">No district data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Professional Status Overview Chart */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                  className="relative overflow-hidden bg-white border shadow-lg backdrop-blur-sm border-gray-200/70 rounded-2xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-emerald-100/50"></div>
                  <div className="relative p-6">
                    <div className="flex items-center mb-6 space-x-3">
                      <div className="p-3 shadow-lg bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                        <ExclamationTriangleIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Status Overview</h3>
                        <p className="text-sm text-gray-600">Processing pipeline</p>
                      </div>
                    </div>

                    {/* Professional Status Pie Chart */}
                    <div className="mb-6">
                      <div className="relative w-48 h-48 mx-auto">
                        <svg viewBox="0 0 200 200" className="w-full h-full transform rotate-90">
                          {(() => {
                            let offset = 0;
                            return [
                              { label: 'Pending', count: stats.pending, color: '#F59E0B' },
                              { label: 'Under Review', count: stats.underReview, color: '#3B82F6' },
                              { label: 'Resolved', count: stats.resolved, color: '#10B981' }
                            ].map((item, index) => {
                              const percentage = stats.total > 0 ? (item.count / stats.total) : 0;
                              const radius = 80;
                              const circumference = 2 * Math.PI * radius;
                              const strokeDasharray = circumference * percentage;
                              const strokeDashoffset = -offset;

                              offset += strokeDasharray;

                              return (
                                <motion.circle
                                  key={item.label}
                                  cx="100"
                                  cy="100"
                                  r={radius}
                                  fill="transparent"
                                  stroke={item.color}
                                  strokeWidth="20"
                                  strokeDasharray={`${strokeDasharray} ${circumference}`}
                                  strokeDashoffset={strokeDashoffset}
                                  className="transition-all duration-300 hover:stroke-width-[25] filter drop-shadow-sm"
                                  initial={{ strokeDasharray: `0 ${circumference}` }}
                                  animate={{ strokeDasharray: `${strokeDasharray} ${circumference}` }}
                                  transition={{ duration: 1.5, delay: 0.9 + index * 0.3 }}
                                />
                              );
                            });
                          })()}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.total)}</div>
                            <div className="text-xs text-gray-500">Cases</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {[
                        { label: 'Pending Review', count: stats.pending, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600', icon: '⏳' },
                        { label: 'Under Review', count: stats.underReview, color: 'bg-gradient-to-r from-blue-500 to-blue-600', icon: '👁️' },
                        { label: 'Resolved', count: stats.resolved, color: 'bg-gradient-to-r from-green-500 to-green-600', icon: '✅' }
                      ].map((item, index) => {
                        const percentage = stats.total > 0 ? (item.count / stats.total * 100) : 0;
                        return (
                          <motion.div
                            key={item.label}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.5 + index * 0.2 }}
                            className="p-3 transition-all duration-200 bg-white rounded-xl hover:shadow-md hover:-translate-y-0.5"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <div className={`w-3 h-3 rounded-full shadow-sm ${item.color}`}></div>
                                <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                                <span className="text-xs">{item.icon}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-gray-900">{formatNumberWithCommas(item.count)}</div>
                                <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                              </div>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full">
                              <motion.div
                                className={`h-2 rounded-full shadow-inner ${item.color}`}
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.max(percentage, 2)}%` }}
                                transition={{ duration: 1, delay: 1.8 + index * 0.2 }}
                              />
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Professional Results Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6 }}
              className="flex items-center justify-between p-6 mt-8 bg-white border shadow-lg backdrop-blur-sm border-gray-200/70 rounded-2xl"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 shadow-lg bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl">
                  <DocumentTextIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {formatNumberWithCommas(filteredIncidents.length)} of {formatNumberWithCommas(incidents.length)} incidents
                  </p>
                  <p className="text-sm font-medium text-gray-600">
                    {filteredIncidents.length === incidents.length ? 'Showing all incident reports' : 'Filtered results'}
                  </p>
                </div>
              </div>

              {(filters.search || filters.district || filters.priority || filters.status || filters.incidentType || filters.dateFrom || filters.dateTo) && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={clearFilters}
                  className="flex items-center px-4 py-2 space-x-2 text-sm font-medium text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-red-500 to-pink-600 rounded-xl hover:from-red-600 hover:to-pink-700 hover:shadow-xl"
                >
                  <XMarkIcon className="w-4 h-4" />
                  <span>Clear Filters</span>
                </motion.button>
              )}
            </motion.div>

            {/* Professional Incidents Table */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7 }}
              className="mt-8 overflow-hidden border shadow-lg backdrop-blur-sm border-gray-200/70 rounded-2xl"
            >
              {/* Professional Table Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 shadow-sm bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                      <DocumentTextIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Incident Reports</h3>
                      <p className="text-sm font-medium text-gray-600">{formatNumberWithCommas(filteredIncidents.length)} active records</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                      Live Data
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={exportToExcel}
                      className="flex items-center px-4 py-2 space-x-2 text-sm font-medium text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl hover:from-emerald-600 hover:to-green-700 hover:shadow-xl"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      <span>Export</span>
                    </motion.button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                        <div className="flex items-center space-x-1">
                          <span>Incident Details</span>
                          <ExclamationTriangleIcon className="w-4 h-4" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                        <div className="flex items-center space-x-1">
                          <span>Type & Priority</span>
                          <TagIcon className="w-4 h-4" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                        <div className="flex items-center space-x-1">
                          <span>Location</span>
                          <MapPinIcon className="w-4 h-4" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          <CheckBadgeIcon className="w-4 h-4" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                        <div className="flex items-center space-x-1">
                          <span>Date</span>
                          <CalendarIcon className="w-4 h-4" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-right text-gray-700 uppercase">
                        <span>Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {paginatedIncidents.map((incident, index) => (
                        <motion.tr
                          key={incident.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ delay: index * 0.05 }}
                          className="transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 group"
                        >
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              <div className="text-sm font-semibold text-gray-900 transition-colors duration-200 group-hover:text-indigo-900">
                                {incident.title || 'No title'}
                              </div>
                              <div className="max-w-xs text-sm text-gray-600 truncate transition-colors duration-200 group-hover:text-gray-700">
                                {incident.description || 'No description'}
                              </div>
                              {(incident.files?.length > 0 || incident.attachments > 0) && (
                                <div className="flex items-center space-x-1">
                                  <PaperClipIcon className="w-3 h-3 text-blue-500" />
                                  <span className="text-xs font-medium text-blue-600">
                                    {incident.files?.length || incident.attachments || 0} attachment{(incident.files?.length || incident.attachments) !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              <div className="text-sm font-medium text-gray-900">{incident.category || 'Unknown'}</div>
                              <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full shadow-sm ${getPriorityColor(incident.severity)}`}>
                                {incident.severity?.charAt(0)?.toUpperCase() + incident.severity?.slice(1) || 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-gray-900">{incident.district || 'Unknown'}</div>
                              <div className="text-sm text-gray-600">{incident.center || incident.location || 'No location'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full shadow-sm ${getIncidentStatusColor(incident.status)}`}>
                              {incident.status?.replace('_', ' ') || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-700">
                            {incident.created_at ? new Date(incident.created_at).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Unknown date'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-end space-x-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleViewIncident(incident)}
                                className="p-2 text-blue-600 transition-all duration-200 rounded-xl hover:text-blue-800 hover:bg-blue-100"
                                title="View details"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleEditIncident(incident)}
                                className="p-2 text-green-600 transition-all duration-200 rounded-xl hover:text-green-800 hover:bg-green-100"
                                title="Edit report"
                              >
                                <CheckIcon className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Simple Pagination */}
              {totalFilteredIncidents > itemsPerPage && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  {/* Results info */}
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalFilteredIncidents)} of {totalFilteredIncidents} results
                  </div>

                  {/* Simple pagination buttons */}
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>

                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Professional Empty States */}
              {filteredIncidents.length === 0 && incidents.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-16 text-center bg-gradient-to-br from-blue-50 to-indigo-100"
                >
                  <div className="p-4 mx-auto mb-6 bg-white rounded-full shadow-lg w-fit">
                    <FunnelIcon className="w-12 h-12 text-indigo-500" />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-gray-900">No incidents match your filters</h3>
                  <p className="mb-6 text-sm text-gray-600">
                    Try adjusting your search criteria or clear the filters to see all incidents.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={clearFilters}
                    className="px-6 py-3 font-medium text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl hover:from-indigo-600 hover:to-purple-700 hover:shadow-xl"
                  >
                    Clear All Filters
                  </motion.button>
                </motion.div>
              )}

              {incidents.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-16 text-center bg-gradient-to-br from-gray-50 to-slate-100"
                >
                  <div className="p-4 mx-auto mb-6 bg-white rounded-full shadow-lg w-fit">
                    <ExclamationTriangleIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-gray-900">No incident reports found</h3>
                  <p className="mb-6 text-sm text-gray-600">
                    Get started by creating your first incident report to track and manage incidents effectively.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNewIncident}
                    className="flex items-center px-6 py-3 mx-auto space-x-2 font-medium text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl hover:from-emerald-600 hover:to-green-700 hover:shadow-xl"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Create Incident Report</span>
                  </motion.button>
                </motion.div>
              )}
            </motion.div>

            {/* Incident View Modal */}
            {showViewModal && selectedIncident && (
              <IncidentViewModal
                incident={selectedIncident}
                onClose={handleCloseViewModal}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Incidents;
